import { ADMIN_FOUND, type AdminPlayer, type AdminPlayersView } from '@gardener/protocol';
import { createEffect, createResource, createSignal, For, type JSX, on, onCleanup, Show } from 'solid-js';
import { call } from './api';
import { go, route } from './nav';
import {
  ago,
  ask,
  Copy,
  EmptyState,
  Icon,
  IconButton,
  LoadingLine,
  optimistic,
  SettingsCard,
  SettingsItem,
  Switch,
  toast,
  Well,
  when,
} from './ui';

const path = (p: AdminPlayer) => `/api/admin/players/${encodeURIComponent(p.id)}`;
/** A search runs this long after the last key (ms). */
const WAIT_MS = 250;

/** What an account is, as logdash's small badges: banned, admin, partner, creator, bot. */
export function PlayerBadges(props: { p: AdminPlayer }) {
  return (
    <span class="flex shrink-0 items-center gap-1 empty:hidden">
      <Show when={props.p.banned}>
        <span class="ld-badge" data-size="sm" data-variant="error">
          banned
        </span>
      </Show>
      <Show when={props.p.admin}>
        <span class="ld-badge" data-size="sm" style={{ '--badge-tone': 'var(--info)' }}>
          admin
        </span>
      </Show>
      <Show when={props.p.partner}>
        <span class="ld-badge" data-size="sm" data-variant="success">
          partner
        </span>
      </Show>
      <Show when={props.p.creator}>
        <span class="ld-badge gap-1" data-size="sm" data-variant="outline">
          <Icon name="twitch" class="size-3" />
          creator
        </span>
      </Show>
      <Show when={props.p.bot}>
        <span class="ld-badge" data-size="sm" data-variant="outline">
          bot
        </span>
      </Show>
    </span>
  );
}

/** logdash's `VisitorAvatar`: the name's first letters on a gradient of the account's own hue. */
export function Avatar(props: { p: Pick<AdminPlayer, 'id' | 'name'>; large?: boolean }) {
  const hue = () => [...props.p.id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 0) % 360;
  return (
    <span
      class={`text-fg-inverse flex shrink-0 items-center justify-center rounded-full font-semibold ${props.large ? 'size-12 text-sm' : 'size-9 text-[11px]'}`}
      style={{
        'background-image': `linear-gradient(135deg, hsl(${hue()} 70% 72%), hsl(${(hue() + 60) % 360} 65% 58%))`,
      }}
      aria-hidden="true"
    >
      {props.p.name.slice(0, 2).toUpperCase()}
    </span>
  );
}

/**
 * Accounts (ADR-0150): found as you type by id, name, login, friend code or linked Twitch; one opened (`#players/<id>`)
 * in a panel where it is banned or let back (ADR-0128), made a partner (ADR-0149) or an admin or no longer, or deleted
 * for being under 13 (ADR-0122). Every change shows at once and is taken back if the server refuses it (ADR-0154).
 * Without a search, every banned account, the latest first.
 */
export function Players() {
  const [q, setQ] = createSignal('');
  const [found, setFound] = createSignal<AdminPlayer[] | null>(null);
  const [searching, setSearching] = createSignal(false);
  const [bans, { mutate: setBans }] = createResource(() => call<AdminPlayersView>('GET', '/api/admin/bans'));
  const [open, setOpen] = createSignal<AdminPlayer | null>(null);

  // As you type: the latest answer wins.
  let asked = 0;
  createEffect(
    on(q, (text) => {
      const term = text.trim();
      const mine = ++asked;
      if (!term) {
        setFound(null);
        setSearching(false);
        return;
      }
      setSearching(true);
      const timer = setTimeout(async () => {
        try {
          const answer = await call<AdminPlayersView>('GET', `/api/admin/players?q=${encodeURIComponent(term)}`);
          if (mine === asked) setFound(answer.players);
        } catch (err) {
          if (mine === asked) toast((err as Error).message, 'error');
        } finally {
          if (mine === asked) setSearching(false);
        }
      }, WAIT_MS);
      onCleanup(() => clearTimeout(timer));
    }),
  );

  // The account in the address: from a list it is on at once, then as the server has it now.
  createEffect(
    on(
      () => route().arg,
      async (id) => {
        if (!id) return setOpen(null);
        const listed = [...(found() ?? []), ...(bans()?.players ?? [])].find((p) => p.id === id);
        setOpen(listed ?? null);
        try {
          const answer = await call<AdminPlayersView>('GET', `/api/admin/players?q=${encodeURIComponent(id)}`);
          if (route().arg === id) setOpen(answer.players.find((p) => p.id === id) ?? null);
        } catch (err) {
          toast((err as Error).message, 'error');
        }
      },
    ),
  );

  /** Account `id` as `change` makes it, wherever it is shown; returns how to take it back. */
  const edit = (id: string, change: (p: AdminPlayer) => AdminPlayer): (() => void) => {
    const before = { found: found(), bans: bans(), open: open() };
    const map = (list: AdminPlayer[]) => list.map((p) => (p.id === id ? change(p) : p));
    const f = found();
    if (f) setFound(map(f));
    const o = open();
    if (o?.id === id) setOpen(change(o));
    const b = bans();
    if (b) setBans({ players: map(b.players).filter((p) => p.banned) });
    return () => {
      setFound(before.found);
      setBans(before.bans);
      setOpen(before.open);
    };
  };
  /** The server's account after a change, wherever it is shown. */
  const settle = (after: AdminPlayer) => void edit(after.id, () => after);

  async function ban(p: AdminPlayer) {
    const reason = await ask({
      title: `Ban ${p.name}?`,
      body: 'Their game closes at once, and the Discord and Twitch they linked are banned with them.',
      input: { label: 'Reason (kept with the ban)', placeholder: 'Cheating in ranked' },
      confirm: 'Ban',
      danger: true,
    });
    if (!reason) return;
    const banned = { ...p, banned: { at: new Date().toISOString(), reason } };
    await optimistic(
      () => {
        const undo = edit(p.id, () => banned);
        const b = bans();
        if (b && !b.players.some((x) => x.id === p.id)) setBans({ players: [banned, ...b.players] });
        return undo;
      },
      () => call('POST', `${path(p)}/ban`, { reason }),
      () => toast(`${p.name} is banned`),
    );
  }
  async function unban(p: AdminPlayer) {
    const sure = await ask({
      title: `Lift ${p.name}'s ban?`,
      body: `Banned for: ${p.banned?.reason ?? ''}. They can sign in and play again at once.`,
      confirm: 'Lift the ban',
    });
    if (sure === null) return;
    await optimistic(
      () => edit(p.id, (x) => ({ ...x, banned: null })),
      () => call('DELETE', `${path(p)}/ban`),
      () => toast(`${p.name} can play again`),
    );
  }
  async function role(p: AdminPlayer, role: 'partner' | 'admin', on: boolean) {
    // Making a partner gives and takes nothing worn: no question. The rest is asked first.
    if (role === 'admin' || !on) {
      const sure = await ask({
        title:
          role === 'admin'
            ? on
              ? `Make ${p.name} an admin?`
              : `${p.name} is no admin any more?`
            : `${p.name} is no partner any more?`,
        body:
          role === 'admin'
            ? on
              ? 'They can use this app: codes, bans, deletions, skins, partners and admins.'
              : 'They lose this app at once.'
            : 'What they wear of the Store’s comes off; what they bought stays.',
        confirm: on ? 'Make admin' : 'Take it back',
        danger: !on,
      });
      if (sure === null) return;
    }
    await optimistic(
      () => edit(p.id, (x) => ({ ...x, [role]: on })),
      () => call<AdminPlayer>('PATCH', path(p), { [role]: on }),
      (after) => {
        settle(after);
        toast(`${p.name} ${on ? 'is' : 'is no longer'} ${role === 'admin' ? 'an admin' : 'a partner'}`);
      },
    );
  }
  async function remove(p: AdminPlayer) {
    const typed = await ask({
      title: `Delete ${p.name}'s account for good?`,
      body: 'For a player found to be under 13: their rounds, friends and tapes go, and their Discord can’t make another account.',
      input: { label: `Type ${p.name} to delete it`, match: p.name },
      confirm: 'Delete for good',
      danger: true,
    });
    if (typed === null) return;
    await optimistic(
      () => {
        const before = { found: found(), bans: bans(), open: open() };
        setFound(found()?.filter((x) => x.id !== p.id) ?? null);
        const b = bans();
        if (b) setBans({ players: b.players.filter((x) => x.id !== p.id) });
        setOpen(null);
        return () => {
          setFound(before.found);
          setBans(before.bans);
          setOpen(before.open);
        };
      },
      () => call('DELETE', path(p)),
      () => {
        toast(`${p.name}'s account is deleted`);
        if (route().arg === p.id) go('players');
      },
    );
  }

  const rows = () => (q().trim() ? (found() ?? []) : (bans()?.players ?? []));
  /** The list is the banned (no search): its rows show the ban, not the account. */
  const banList = () => !q().trim();
  /** When the row's time says: the ban on the banned list, else the account last seen. */
  const at = (p: AdminPlayer) => (banList() && p.banned ? p.banned.at : p.lastSeenAt);

  return (
    <div class="@container flex w-full flex-col">
      <div class="grid grid-cols-1 items-start gap-2 p-2 @4xl:grid-cols-[minmax(0,1fr)_26rem]">
        <Well label="Players">
          <label class="focus-within:inset-ring-surface-input-hover-border edge flex h-8 min-w-24 items-center gap-2 rounded-full px-3 text-sm focus-within:inset-ring">
            <Icon name="search" class="text-fg-faint size-3.5 shrink-0" />
            <input
              type="text"
              value={q()}
              placeholder="Name, id, login, friend code or Twitch"
              aria-label="Find a player"
              class="placeholder:text-fg-faint h-full min-w-0 flex-1 bg-transparent outline-none"
              onInput={(e) => setQ(e.currentTarget.value)}
            />
            <Show when={searching()}>
              <span class="ld-spinner text-fg-muted" data-size="xs" role="status" aria-label="Searching" />
            </Show>
          </label>
          <header class="flex h-7 shrink-0 items-center justify-between gap-3">
            <h2 class="truncate px-3 text-[13px] font-medium">{banList() ? 'Banned, the latest first' : 'Found'}</h2>
            <Show when={rows().length}>
              <span class="text-fg-muted px-3 text-xs tabular-nums">
                {rows().length} {rows().length === 1 ? 'account' : 'accounts'}
              </span>
            </Show>
          </header>
          <Show
            when={rows().length}
            fallback={
              <Show
                when={banList() ? !bans.loading : !searching()}
                fallback={
                  <div class="px-3 py-2.5">
                    <LoadingLine label={banList() ? 'Loading the banned' : 'Searching'} />
                  </div>
                }
              >
                {banList() ? (
                  <EmptyState centered title="Nobody is banned" description="Search for a player to ban one." />
                ) : (
                  <EmptyState
                    centered
                    title="Nobody found"
                    description="No account’s name, id, login, friend code or Twitch matches this."
                  />
                )}
              </Show>
            }
          >
            <div class="@container">
              <table class="w-full table-fixed text-left text-sm">
                <thead class="text-fg-muted text-xs">
                  <tr>
                    <th scope="col" class="h-6 px-3 font-normal">
                      Player
                    </th>
                    <th scope="col" class="hidden h-6 w-1/4 px-3 font-normal @xl:table-cell">
                      Login
                    </th>
                    <th scope="col" class="hidden h-6 w-36 px-3 font-normal @md:table-cell">
                      {banList() ? 'Banned' : 'Last seen'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <For each={rows()}>
                    {(p) => (
                      <tr
                        class="group relative"
                        classList={{
                          'bg-surface-25-selected-bg': open()?.id === p.id,
                          'hover:bg-surface-25-hover-bg': open()?.id !== p.id,
                        }}
                      >
                        <td class="rounded-lg px-3 py-2 @md:rounded-r-none">
                          <div class="flex min-w-0 items-center gap-3">
                            <Avatar p={p} />
                            <div class="min-w-0 flex-1">
                              <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                <button
                                  type="button"
                                  class="focus-visible:outline-brand block max-w-full min-w-0 cursor-pointer truncate text-left font-medium outline-none after:absolute after:inset-0 group-hover:underline focus-visible:after:rounded-lg focus-visible:after:outline-2 focus-visible:after:-outline-offset-2"
                                  aria-current={open()?.id === p.id ? 'true' : undefined}
                                  onClick={() => go('players', p.id)}
                                >
                                  {p.name}
                                </button>
                                <PlayerBadges p={p} />
                                <span
                                  class="text-fg-tertiary ml-auto shrink-0 pl-1 text-xs whitespace-nowrap @md:hidden"
                                  title={when(at(p))}
                                >
                                  {ago(at(p))}
                                </span>
                              </div>
                              <Show when={banList() && p.banned}>
                                {(b) => <p class="text-fg-muted mt-0.5 truncate text-xs">{b().reason}</p>}
                              </Show>
                            </div>
                          </div>
                        </td>
                        <td class="text-fg-tertiary hidden truncate px-3 py-2 @xl:table-cell">{p.login ?? '–'}</td>
                        <td class="hidden rounded-r-lg px-3 py-2 whitespace-nowrap @md:table-cell" title={when(at(p))}>
                          {ago(at(p))}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
          <Show when={(found()?.length ?? 0) >= ADMIN_FOUND}>
            <p class="text-fg-muted px-3 pb-1 text-xs">
              The first {ADMIN_FOUND} only: search for an id, login or friend code.
            </p>
          </Show>
        </Well>
        <div
          class="flex min-w-0 flex-col gap-2 @max-4xl:order-first @4xl:sticky @4xl:top-2"
          classList={{ '@max-4xl:hidden': !route().arg }}
        >
          <Show
            when={open()}
            fallback={
              <Well label="Account">
                <Show
                  when={route().arg}
                  fallback={
                    <EmptyState centered title="No player open" description="Pick a player to see their account." />
                  }
                >
                  <div class="px-3 py-2.5">
                    <LoadingLine label="Loading the account" />
                  </div>
                </Show>
              </Well>
            }
          >
            {(p) => (
              <PlayerPanel p={p()} ban={ban} unban={unban} role={role} remove={remove} close={() => go('players')} />
            )}
          </Show>
        </div>
      </div>
    </div>
  );
}

/**
 * One account, as logdash draws a visitor (`VisitorDialog`: the avatar, name and a strip of dates) over its settings
 * cards (`ClusterSettings`): how it signs in, its roles, and the danger zone.
 */
function PlayerPanel(props: {
  p: AdminPlayer;
  ban: (p: AdminPlayer) => void;
  unban: (p: AdminPlayer) => void;
  role: (p: AdminPlayer, role: 'partner' | 'admin', on: boolean) => void;
  remove: (p: AdminPlayer) => void;
  close: () => void;
}) {
  const p = () => props.p;
  /** A label and its value, as logdash's `ProjectInfoCard` rows; `none` where there is nothing. */
  const Field = (f: { label: string; value: string | null; none: string; mono?: boolean; action?: JSX.Element }) => (
    <SettingsItem action={f.value ? f.action : undefined}>
      <div class="flex min-w-0 items-center gap-3">
        <span class="text-fg-muted w-16 shrink-0">{f.label}</span>
        <Show when={f.value} fallback={<span class="text-fg-muted truncate">{f.none}</span>}>
          <span class="truncate" classList={{ 'font-mono': f.mono }} title={f.value ?? ''}>
            {f.value}
          </span>
        </Show>
      </div>
    </SettingsItem>
  );
  return (
    <>
      <section class="bg-surface-25-bg flex min-w-0 flex-col rounded-2xl p-2" aria-label={p().name}>
        <header class="flex items-start gap-4 px-3 pt-3 pb-4">
          <Avatar p={p()} large />
          <div class="min-w-0 flex-1">
            <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <h2 class="max-w-full truncate text-base font-semibold">{p().name}</h2>
              <PlayerBadges p={p()} />
            </div>
            <p class="text-fg-muted mt-0.5 truncate text-xs">{p().login ? `@${p().login}` : 'No login'}</p>
          </div>
          <IconButton label="Close" well class="-mt-1 -mr-1.5" onClick={props.close}>
            <Icon name="x" class="size-4" />
          </IconButton>
        </header>
        <Show when={p().banned}>
          {(b) => (
            <div class="px-3 pb-4">
              <div class="ld-alert" data-variant="error" role="alert">
                <span>
                  <span class="font-medium">Banned {ago(b().at)}:</span> {b().reason}
                </span>
              </div>
            </div>
          )}
        </Show>
        <dl class="edge-t edge-between-x grid grid-cols-2 text-sm">
          <div class="px-3 py-2.5">
            <dt class="text-fg-muted text-xs">Joined</dt>
            <dd class="mt-0.5" title={when(p().createdAt)}>
              {ago(p().createdAt)}
            </dd>
          </div>
          <div class="px-3 py-2.5">
            <dt class="text-fg-muted text-xs">Last seen</dt>
            <dd class="mt-0.5" title={when(p().lastSeenAt)}>
              {ago(p().lastSeenAt)}
            </dd>
          </div>
        </dl>
      </section>
      <SettingsCard title="Account" description="Its id, how it signs in and what it linked.">
        <Field
          label="ID"
          value={p().id}
          none="None"
          mono
          action={<Copy text={p().id} label="Copy the id" well class="-mr-1.5" />}
        />
        <Field label="Login" value={p().login} none="None" />
        <Field label="Discord" value={p().discord} none="Not linked" />
        <Field
          label="Twitch"
          value={p().twitch}
          none="Not linked"
          action={
            <IconButton well label="Open on Twitch" href={`https://twitch.tv/${p().twitch}`} class="-mr-1.5">
              <Icon name="external" class="size-3.5" />
            </IconButton>
          }
        />
      </SettingsCard>
      <SettingsCard title="Roles" description="What the account is beyond a player.">
        <SettingsItem
          action={
            <Switch
              id="role-partner"
              size="xs"
              checked={p().partner}
              label="Partner"
              onChange={(on) => props.role(p(), 'partner', on)}
            />
          }
        >
          <label for="role-partner" class="block cursor-pointer select-none">
            <span class="block">Partner</span>
            <span class="text-fg-muted block">Everything the Store sells is theirs while they are.</span>
          </label>
        </SettingsItem>
        <SettingsItem
          action={
            <Switch
              id="role-admin"
              size="xs"
              checked={p().admin}
              label="Admin"
              onChange={(on) => props.role(p(), 'admin', on)}
            />
          }
        >
          <label for="role-admin" class="block cursor-pointer select-none">
            <span class="block">Admin</span>
            <span class="text-fg-muted block">Can use this app.</span>
          </label>
        </SettingsItem>
      </SettingsCard>
      <SettingsCard title="Danger zone" description="Lock the account out, or delete it for good." danger>
        <Show
          when={p().banned}
          fallback={
            <SettingsItem
              action={
                <button
                  type="button"
                  class="ld-button"
                  data-variant="danger"
                  data-size="sm"
                  onClick={() => props.ban(p())}
                >
                  Ban
                </button>
              }
            >
              <p>Ban</p>
              <p class="text-fg-muted">Their game closes at once; their Discord and Twitch are banned with them.</p>
            </SettingsItem>
          }
        >
          <SettingsItem
            action={
              <button type="button" class="ld-button" data-size="sm" onClick={() => props.unban(p())}>
                Lift the ban
              </button>
            }
          >
            <p>Lift the ban</p>
            <p class="text-fg-muted">They can sign in and play again at once.</p>
          </SettingsItem>
        </Show>
        <SettingsItem
          action={
            <button
              type="button"
              class="ld-button"
              data-variant="danger"
              data-size="sm"
              onClick={() => props.remove(p())}
            >
              Delete
            </button>
          }
        >
          <p>Delete (under 13)</p>
          <p class="text-fg-muted">Their rounds, friends and tapes go for good.</p>
        </SettingsItem>
      </SettingsCard>
    </>
  );
}
